import { ThingDataSubscriptionInfo } from '@eagle/core-data-types';
import { Chip } from '@ethings/ui-toolkit';
import { Card, CardContent } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticated } from '../../auth';
import { MiddleSpinner } from '../../components';
import { evaluate } from '../../evaluator';
import { useUiTemplate } from '../../hooks';
import { CONTRACT_STATUS_CARD_DEFAULT_TEMPLATE } from '../../ui-templates/contract-status-card';
import { getContractStatus, statusToChipDefinition } from './contract-management.utils';

interface ContractStatusCardProps {
  dataSubscriptionInfo: ThingDataSubscriptionInfo;
  thingTypeId: string;
}

export const ContractStatusCard: FC<ContractStatusCardProps> = ({ dataSubscriptionInfo, thingTypeId }) => {
  const { restClient } = useAuthenticated();
  const { template, loaded } = useUiTemplate('contract-status-card', CONTRACT_STATUS_CARD_DEFAULT_TEMPLATE);
  const { data: dataSubscriptionPlans, isLoading } = useQuery({
    queryKey: ['data-subscription-plans-for-thing-type', thingTypeId],
    queryFn: () => {
      return restClient.dataSubscriptionPlan.getForThingType(thingTypeId);
    },
    staleTime: Infinity,
  });

  const statusChip = <StatusChip dataSubscriptionInfo={dataSubscriptionInfo} />;
  const plan = dataSubscriptionPlans?.items.find((plan) => plan.dataSubscriptionPlanId === dataSubscriptionInfo.lastDataSubscription?.dataSubscriptionPlanId);

  return (
    <Card data-testid="contract-status-card">
      <CardContent>
        <>{(loaded && !isLoading) ? evaluate(template, { statusChip, plan, dataSubscriptionInfo }) : <MiddleSpinner />}</>
      </CardContent>
    </Card>
  );
};

export const StatusChip: FC<{ dataSubscriptionInfo: ThingDataSubscriptionInfo }> = ({ dataSubscriptionInfo }) => {
  const { t } = useTranslation(['common']);
  const status = getContractStatus(dataSubscriptionInfo);
  const chipDefinition = statusToChipDefinition[status];

  return (
    <Chip
      data-testid="contract-status-card-status-chip"
      color={chipDefinition.color}
      label={t(chipDefinition.label)}
    />
  );
};
